import { useFetch } from './useFetch';

export interface UserPreferences {
  collapseSidebar: boolean;
  useNewDesign: boolean;
  crmCreateContactDynamicSearch: boolean;
  crmCreateAccountDynamicSearch: boolean;
  crmCreateOpportunityFromAccount: boolean;
  crmActivityDaysAheadProject: number;
  nylasEmailSync: boolean;
  nylasEmailRetentionDays: number;
}

// Server clamps to this range; mirrored here so the FE can present the same bounds
// and avoid round-trips that the BE will just reject / clamp anyway.
export const NYLAS_EMAIL_RETENTION_MIN = 7;
export const NYLAS_EMAIL_RETENTION_MAX = 90;
export const NYLAS_EMAIL_RETENTION_DEFAULT = 90;

interface UserPreferencePayload {
  collapseSidebar?: 'so' | 'hto';
  useNewDesign?: 'yes' | 'no';
  selectedOffices?: string[];
  updateTimezone?: string;
  aiDisclaimerAccepted?: 'yes' | 'no';
  crmCreateContactDynamicSearch?: 'yes' | 'no';
  crmCreateAccountDynamicSearch?: 'yes' | 'no';
  crmCreateOpportunityFromAccount?: 'yes' | 'no';
  crmActivityDaysAheadProject?: number;
  nylasEmailSync?: 'yes' | 'no';
  nylasEmailRetentionDays?: number;
}

interface UpdateResponse {
  success: boolean;
  updatedSidebar: boolean;
  updatedOffices: boolean;
  updatedDesign: boolean;
  updatedTimezone: boolean;
  updatedCrmCreateAccountDynamicSearch: boolean;
  updatedCrmCreateContactDynamicSearch: boolean;
  updatedCrmCreateOpportunityFromAccount: boolean;
  updatedCrmActivityDaysAheadProject: boolean;
  updatedNylasEmailSync: boolean;
  updatedNylasEmailRetentionDays: boolean;
}

type SettingKey = 'yes' | 'no' | 'hto' | 'so';
type SettingMap = {[K in SettingKey]: boolean };

export const useUserPreferences = () => {
  const { get, put } = useFetch();

  const settingMap: SettingMap = {
    yes: true,
    no: false,
    hto: true,
    so: false,
  };

  const getSettingOrDefault = (setting: SettingKey, defaultValue: boolean) => {
    if (!(setting in settingMap)) return defaultValue;
    return settingMap[setting];
  };

  const clampRetentionDays = (value: unknown): number => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) {
      return NYLAS_EMAIL_RETENTION_DEFAULT;
    }
    return Math.max(NYLAS_EMAIL_RETENTION_MIN, Math.min(NYLAS_EMAIL_RETENTION_MAX, Math.round(numeric)));
  };

  const getPreferences = async (): Promise<UserPreferences> => {
    const preferences = await get<UserPreferencePayload>('/user-preferences');

    return {
      collapseSidebar: getSettingOrDefault(preferences.collapseSidebar, false),
      useNewDesign: getSettingOrDefault(preferences.useNewDesign, false),
      crmCreateAccountDynamicSearch: getSettingOrDefault(preferences.crmCreateAccountDynamicSearch, true),
      crmCreateContactDynamicSearch: getSettingOrDefault(preferences.crmCreateContactDynamicSearch, true),
      crmCreateOpportunityFromAccount: getSettingOrDefault(preferences.crmCreateOpportunityFromAccount, true),
      crmActivityDaysAheadProject: preferences.crmActivityDaysAheadProject ?? 7,
      nylasEmailSync: getSettingOrDefault(preferences.nylasEmailSync, false),
      nylasEmailRetentionDays: clampRetentionDays(preferences.nylasEmailRetentionDays),
    };
  };

  const updateCollapsePreference = async (selection: 'so' | 'hto') => {
    const result = await put<{ updatedSidebar: boolean }>('/user-preferences', { collapseSidebar: selection });
    return result.updatedSidebar;
  };

  const updateOfficeSelection = async (offices: string) => {
    const data = await put<{ updatedOffices: boolean }>('/user-preferences', { selectedOffices: offices });
    return data?.updatedOffices;
  };

  const updateDesignPreference = async (useNewDesign: boolean) => {
    const data = await put<{ updatedDesign: boolean }>('/user-preferences', { useNewDesign: useNewDesign ? 'yes' : 'no' });
    return data.updatedDesign;
  };

  const updatePreference = async (prefs: UserPreferencePayload) => await put('/user-preferences', prefs) as UpdateResponse;

  const booleanToYesno = (value: boolean) => value ? 'yes' : 'no';

  const yesnoToBoolean = (value: 'yes' | 'no') => value === 'yes' ? true : false;

  return {
    booleanToYesno,
    clampRetentionDays,
    getPreferences,
    updateCollapsePreference,
    updateDesignPreference,
    updateOfficeSelection,
    updatePreference,
    yesnoToBoolean,
  };
};
